import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GripVertical, Trash2, ArrowUp, ArrowDown, Plus, Heading1, Type, Image as ImageIcon, MousePointerClick, Minus } from "lucide-react";

const BLOCK_TYPES = [
  { key: "heading", label: "Títol", icon: Heading1 },
  { key: "text", label: "Paràgraf", icon: Type },
  { key: "image", label: "Imatge", icon: ImageIcon },
  { key: "button", label: "Botó", icon: MousePointerClick },
  { key: "divider", label: "Separador", icon: Minus },
];

const defaultProps = {
  heading: { text: "Novetats de Shopping Salou", align: "left" },
  text: { text: "Escriu el missatge aquí. Suporta línies múltiples i emojis." },
  image: { url: "https://images.unsplash.com/photo-1565464089519-da4089e07759?w=600", alt: "" },
  button: { text: "Descobreix-ho", url: "https://www.shoppingsalou.com" },
  divider: {},
};

function safeUrl(u) {
  const s = String(u || "").trim();
  if (/^https?:\/\//i.test(s) || /^mailto:/i.test(s)) return s;
  return "#";
}

export function blocksToHtml(blocks) {
  const inner = blocks.map((b) => {
    switch (b.type) {
      case "heading":
        return `<h1 style="font-family:Arial,sans-serif;color:#CF0283;margin:0 0 16px;font-size:24px;text-align:${b.props.align || "left"};">${escapeHtml(b.props.text)}</h1>`;
      case "text":
        return `<p style="font-family:Arial,sans-serif;color:#2A0619;line-height:1.6;margin:0 0 16px;font-size:15px;">${escapeHtml(b.props.text).replace(/\n/g, "<br/>")}</p>`;
      case "image":
        return `<div style="margin:0 0 16px;"><img src="${escapeHtml(safeUrl(b.props.url))}" alt="${escapeHtml(b.props.alt || "")}" style="max-width:100%;height:auto;border-radius:12px;display:block;" /></div>`;
      case "button":
        return `<div style="margin:12px 0 20px;text-align:center;"><a href="${escapeHtml(safeUrl(b.props.url))}" style="display:inline-block;background:#CF0283;color:#ffffff;padding:12px 28px;border-radius:999px;text-decoration:none;font-family:Arial,sans-serif;font-weight:600;">${escapeHtml(b.props.text)}</a></div>`;
      case "divider":
        return `<hr style="border:none;border-top:1px solid #FBD3E8;margin:16px 0;" />`;
      default:
        return "";
    }
  }).join("\n");
  return `<table width="100%" cellpadding="0" cellspacing="0" style="background:#FCFBF9;padding:24px 0;font-family:Arial,sans-serif;">
    <tr><td align="center">
      <table width="560" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:16px;overflow:hidden;">
        <tr><td style="background:#CF0283;padding:20px 24px;color:#fff;">
          <div style="font-size:11px;letter-spacing:2px;text-transform:uppercase;color:#FFDE7D;">Shopping Salou</div>
          <div style="font-size:18px;font-weight:700;margin-top:2px;">Associació de Comerciants</div>
        </td></tr>
        <tr><td style="padding:28px 28px;">
          ${inner}
        </td></tr>
        <tr><td style="background:#F4F4F2;padding:14px 24px;color:#64748b;font-size:11px;text-align:center;">
          Shopping Salou · Salou, Tarragona
        </td></tr>
      </table>
    </td></tr>
  </table>`;
}

function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

export default function BlockEditor({ blocks, onChange }) {
  const [selected, setSelected] = useState(null);

  const addBlock = (type) => {
    onChange([...blocks, { id: crypto.randomUUID(), type, props: { ...defaultProps[type] } }]);
  };
  const updateBlock = (id, patch) => {
    onChange(blocks.map((b) => (b.id === id ? { ...b, props: { ...b.props, ...patch } } : b)));
  };
  const removeBlock = (id) => onChange(blocks.filter((b) => b.id !== id));
  const move = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= blocks.length) return;
    const copy = [...blocks];
    [copy[idx], copy[target]] = [copy[target], copy[idx]];
    onChange(copy);
  };

  return (
    <div className="grid md:grid-cols-5 gap-4" data-testid="block-editor">
      <div className="md:col-span-2 space-y-2">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Afegir bloc</div>
        <div className="grid grid-cols-2 gap-2">
          {BLOCK_TYPES.map((t) => (
            <Button key={t.key} type="button" variant="outline" className="justify-start rounded-xl" onClick={() => addBlock(t.key)} data-testid={`block-add-${t.key}`}>
              <t.icon className="w-4 h-4 mr-2" /> {t.label}
            </Button>
          ))}
        </div>

        <div className="mt-6 space-y-2">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Estructura</div>
          {blocks.length === 0 && <div className="text-xs text-muted-foreground py-4">Encara sense blocs.</div>}
          {blocks.map((b, i) => (
            <div key={b.id} className={`p-3 rounded-xl border-2 cursor-pointer ${selected === b.id ? "border-accent bg-accent/10" : "border-border"}`} onClick={() => setSelected(b.id)} data-testid={`block-item-${b.type}`}>
              <div className="flex items-center gap-2 text-sm">
                <GripVertical className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="font-medium capitalize">{b.type}</span>
                <div className="ml-auto flex gap-1">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); move(i, -1); }}><ArrowUp className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); move(i, 1); }}><ArrowDown className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); removeBlock(b.id); }}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
                </div>
              </div>
              {selected === b.id && (
                <div className="mt-3 space-y-2" onClick={(e) => e.stopPropagation()}>
                  {b.type === "heading" && (<>
                    <Input value={b.props.text} onChange={(e) => updateBlock(b.id, { text: e.target.value })} placeholder="Títol" />
                    <Select value={b.props.align || "left"} onValueChange={(v) => updateBlock(b.id, { align: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="left">Esquerra</SelectItem><SelectItem value="center">Centrat</SelectItem><SelectItem value="right">Dreta</SelectItem></SelectContent>
                    </Select>
                  </>)}
                  {b.type === "text" && <Textarea rows={4} value={b.props.text} onChange={(e) => updateBlock(b.id, { text: e.target.value })} />}
                  {b.type === "image" && (<>
                    <Input value={b.props.url} onChange={(e) => updateBlock(b.id, { url: e.target.value })} placeholder="URL imatge" />
                    <Input value={b.props.alt} onChange={(e) => updateBlock(b.id, { alt: e.target.value })} placeholder="Text alternatiu" />
                  </>)}
                  {b.type === "button" && (<>
                    <Input value={b.props.text} onChange={(e) => updateBlock(b.id, { text: e.target.value })} placeholder="Text del botó" />
                    <Input value={b.props.url} onChange={(e) => updateBlock(b.id, { url: e.target.value })} placeholder="URL destí" />
                  </>)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="md:col-span-3">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Vista prèvia</div>
        <div className="rounded-2xl border-2 overflow-hidden bg-slate-50 dark:bg-slate-900/50 p-4 max-h-[560px] overflow-y-auto" data-testid="block-preview">
          <div dangerouslySetInnerHTML={{ __html: blocksToHtml(blocks) }} />
        </div>
      </div>
    </div>
  );
}
